import React from "react";
import { MenuItem, SelectChangeEvent } from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import { useSelector, useDispatch, shallowEqual } from "react-redux";

import {
  CardProps,
  InputTypes,
  removeRequiredCardId,
  setTitle,
  StateProps,
  typeChange,
} from "../../store";
import { extendedCardProps } from "../Card";
import * as S from "./styles";

const CardHeader = ({ id, isTitle }: Pick<extendedCardProps, "id" | "isTitle">) => {
  const { control, register } = useForm();
  const dispatch = useDispatch();

  const requiredCardIds = useSelector((state: StateProps) => state.required as string[]);

  const needToCompleteRequired = requiredCardIds.includes(id);

  const isAddableSection = useSelector((state: StateProps) => {
    const currentCard = state.cards.find((card) => card.id === id) as CardProps;
    return currentCard.inputType === InputTypes.ADDABLE_SECTION;
  });

  const isFocused = useSelector((state: StateProps) => {
    const currentCard = state.cards.find((card) => card.id === id) as CardProps;
    return currentCard.isFocused;
  }, shallowEqual);

  const { cardTitle } = useSelector(
    (state: StateProps) => state.cards.find((card) => card.id === id) as CardProps,
    shallowEqual,
  );

  const { isRequired } = useSelector(
    (state: StateProps) => state.cards.find((card) => card.id === id) as CardProps,
    shallowEqual,
  );

  const { inputType } = useSelector(
    (state: StateProps) => state.cards.find((card) => card.id === id) as CardProps,
    shallowEqual,
  );

  const { lengthMin } = useSelector(
    (state: StateProps) => state.cards.find((card) => card.id === id) as CardProps,
    shallowEqual,
  );

  const getLengthMin = () => {
    if (lengthMin === undefined || lengthMin === null || lengthMin === 0) {
      return '팀원 수 추가 필요';
    }
    return `최대 ${lengthMin}명`;
  }

  const handleCardTitleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    dispatch(setTitle({ cardId: id, text: e.target.value }));
    dispatch(removeRequiredCardId({ cardId: id }));
  };

  const handleInputTypeChange = (e: SelectChangeEvent<unknown>) => {
    dispatch(typeChange({ id, inputType: e.target.value as string }));
  };

  const getAutoWidth = () => getTextWidth("16px Arial, sans-serif", cardTitle) * 1.2;

  const getTextWidth = (font: string, text: string) => {
    // 텍스트를 측정하기 위해 canvas 요소를 생성합니다.
    const canvas = document.createElement("canvas");
    const context = canvas.getContext("2d");

    // 사용하고자 하는 글꼴 설정
    context!.font = font;

    // 텍스트의 너비를 계산하여 반환합니다.
    return context!.measureText(text).width;
  }

  return (
    <S.Container $isFocused={isFocused}>
      <Controller
        name="CardTitle"
        control={control}
        render={() => (
          <>
            <S.TextField
              {...register(id)}
              id="filled-basic"
              $isTitle={isTitle}
              $isSection={inputType === InputTypes.SECTION || inputType === InputTypes.ADDABLE_SECTION}
              $isFocused={isFocused}
              alert={needToCompleteRequired}
              value={cardTitle}
              $autoWidth={getAutoWidth()}
              onChange={(e) => {
                handleCardTitleChange(e);
              }}
              placeholder={isTitle ? "설문지 제목" : "질문"}
              variant="filled"
            />
            {isRequired && !isFocused ? <S.RequireMark>*</S.RequireMark> : null}
            {isAddableSection && !isFocused ? <S.AddableSectionMark>{`팀원 수 (${getLengthMin()})`}</S.AddableSectionMark> : null}
          </>
        )}
      />
      {!isTitle && isFocused ? (
        <Controller
          name="inputTypeSelect"
          control={control}
          render={() => (
            <S.Select onChange={handleInputTypeChange} defaultValue={inputType}>
              <MenuItem value={InputTypes.SECTION}>섹션 제목</MenuItem>
              <MenuItem value={InputTypes.NAME}>이름(필수)</MenuItem>
              <MenuItem value={InputTypes.CONTACT}>연락처(필수)</MenuItem>
              <MenuItem value={InputTypes.TEXT}>단답형</MenuItem>
              <MenuItem value={InputTypes.TEXTAREA}>장문형</MenuItem>
              <MenuItem value={InputTypes.RADIO}>객관식 질문 (단일 선택)</MenuItem>
              <MenuItem value={InputTypes.CHECKBOX}>객관식 질문 (다중 선택)</MenuItem>
              <MenuItem value={InputTypes.SELECT}>드롭다운</MenuItem>
              <MenuItem value={InputTypes.FILE}>파일 첨부(1개)</MenuItem>
              <MenuItem value={InputTypes.ADDABLE_SECTION}>팀원정보(그룹)</MenuItem>
            </S.Select>
          )}
        />
      ) : null}
    </S.Container>
  );
};

export default React.memo(CardHeader);
